import { Activity, Sparkles } from 'lucide-react'
import { createElement, type ReactElement } from 'react'
import type { Space, SpaceModule } from '@/types/spaces'
import { attendanceModule } from './attendance'
import { bookingsModule } from './bookings'
import { call4papersModule } from './call4papers'
import { channelModule } from './channel'
import { certificatesModule } from './certificates'
import { classifiedsModule } from './classifieds'
import { contentModule } from './content'
import { coursesModule } from './courses'
import { developmentPlanModule } from './developmentPlan'
import { downloadsModule } from './downloads'
import { eventsModule } from './events'
import { feedModule } from './feed'
import { feedbacksModule } from './feedbacks'
import { formsModule } from './forms'
import { galleryModule } from './gallery'
import { goalsOkrsModule } from './goalsOkrs'
import { networkingModule } from './networking'
import { oneOnOnesModule } from './oneOnOnes'
import { parcelsModule } from './parcels'
import { podcastModule } from './podcast'
import { performanceReviewModule } from './performanceReview'
import { PLANNED_MODULES } from './planned'
import { rankingModule } from './ranking'
import { recognitionModule } from './recognition'
import { schedulingModule } from './scheduling'
import { storefrontModule } from './storefront'
import { supportModule } from './support'
import { timeTrackingModule } from './timeTracking'
import { surveysModule } from './surveys'
import { upvotesModule } from './upvotes'
import { urlModule } from './url'
import { videosModule } from './videos'
import { visitorsModule } from './visitors'
import type { ModuleDefinition, SpacePageProps } from './types'
import { lazyPage } from '@/lib/lazyPage'

const FeedPage = lazyPage(() => import('@/pages/Feed'), 'FeedPage')

/**
 * The single registry of space modules.
 *
 * To add a module: create `src/modules/<key>/index.tsx` and add one line
 * here; `SpaceModule` (types/spaces.ts) is the keys of this map. The router,
 * the sidebar icon, the creation wizard, the edit dialog, "who can post",
 * one item or several, the workflow trigger blocks and space actions, and the
 * i18n namespaces all read from it. `tests/moduleRegistryGuard.test.ts` fails
 * when a list of module keys shows up anywhere else.
 */
export const MODULES = {
  content: contentModule,
  events: eventsModule,
  courses: coursesModule,
  ranking: rankingModule,
  feed: feedModule,
  url: urlModule,
  gallery: galleryModule,
  call4papers: call4papersModule,
  certificates: certificatesModule,
  storefront: storefrontModule,
  classifieds: classifiedsModule,
  downloads: downloadsModule,
  attendance: attendanceModule,
  bookings: bookingsModule,
  scheduling: schedulingModule,
  parcels: parcelsModule,
  support: supportModule,
  visitors: visitorsModule,
  surveys: surveysModule,
  forms: formsModule,
  recognition: recognitionModule,
  feedbacks: feedbacksModule,
  developmentPlan: developmentPlanModule,
  timeTracking: timeTrackingModule,
  performanceReview: performanceReviewModule,
  goalsOkrs: goalsOkrsModule,
  oneOnOnes: oneOnOnesModule,
  upvotes: upvotesModule,
  channel: channelModule,
  networking: networkingModule,
  podcast: podcastModule,
  videos: videosModule
} satisfies Record<string, ModuleDefinition>

/** Every registered module, in registry order. */
export const registeredModules = (): ModuleDefinition[] => Object.values(MODULES as Record<SpaceModule, ModuleDefinition>)

const PLANNED: Record<string, ModuleDefinition> = Object.fromEntries(
  PLANNED_MODULES.map((module) => [module.key, module])
)

/** Any module the app knows by key: real, announced with a `SpaceModule` value, or only announced. */
export const getModule = (key: string): ModuleDefinition | undefined =>
  (Object.prototype.hasOwnProperty.call(MODULES, key) ? (MODULES as Record<SpaceModule, ModuleDefinition>)[key as SpaceModule] : undefined) ?? PLANNED[key]

/** The app has an entry for it, so the picker can draw a card (live or "coming soon"). */
export const isKnownModule = (key: string): boolean => getModule(key) !== undefined

/** The app can create and render it. */
export const isReadyModule = (key: string): key is SpaceModule => getModule(key)?.ready === true

/** Modules a space can be created with, in registry order. */
export const readyModules = (): ModuleDefinition[] => registeredModules().filter((m) => m.ready)

/**
 * The icon of a card in the module picker. A module the API announces that the
 * app has no entry for still gets a card ("coming soon") with a neutral icon.
 */
export const pickerIcon = (key: string) => getModule(key)?.icon ?? Sparkles

/** An unknown or missing key degrades to a neutral icon instead of blanking the page. */
export const moduleIcon = (key: string) => getModule(key)?.icon ?? Activity
export const moduleIconName = (key: string) => getModule(key)?.iconName ?? 'activity'

/** Every i18n namespace the registered modules use, without repeats. */
export const MODULE_NAMESPACES: string[] = Array.from(
  new Set(registeredModules().flatMap((module) => module.namespaces ?? []))
)

/**
 * The list view of a space. A module with no page (an external link, an
 * unknown value from a newer API) shows the feed, as the router always did.
 */
export const renderSpacePage = (module: string, props: SpacePageProps): ReactElement => {
  const Page = getModule(module)?.ListPage ?? FeedPage
  return createElement(Page, props)
}

/** The detail view of an item, or `null` when the module has none (the caller shows the list). */
export const renderSpaceDetail = (space: Space, params: Record<string, string>): ReactElement | null =>
  getModule(space.module)?.renderDetail?.({ spaceData: space, params }) ?? null

/** "Who can post" means something in this module's spaces. */
export const hasPosting = (key: string | null | undefined): boolean => (key ? getModule(key)?.posting !== false : true)

/** The module's config slice says the space is one item (a call, a course, a survey…). */
export const isSingleItemConfig = (key: string | null | undefined, slice: unknown): boolean => {
  const single = key ? getModule(key)?.singleItem : undefined
  if (!single) return false
  return (slice as Record<string, unknown> | null | undefined)?.[single.modeKey ?? 'mode'] === 'single'
}

/**
 * Whether a one-item space has no item yet. Any doubt (no check for the
 * module, a failed read) answers `false`, so the edit dialog just closes.
 */
export const isSingleSpaceEmpty = async (key: string, spaceId: string): Promise<boolean> => {
  const isEmpty = getModule(key)?.singleItem?.isEmpty
  if (!isEmpty) return false
  try {
    return await isEmpty(spaceId)
  } catch {
    return false
  }
}
